import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { isEqual } from 'lodash-es';
import { AppActions } from 'projects/semeris/src/store/app/app.actions';
import { ConversationActions } from 'projects/semeris/src/store/conversation/conversation.actions';
import { ConversationSelectors } from 'projects/semeris/src/store/conversation/conversation.selectors';
import { Observable, Subject, merge, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

import { ConversationListState, ConversationListStore } from './conversation-list.store';
import { LabelService } from '../../label/services/label.service';
import { WebSocketService } from '../../shared/services/web-socket/web-socket.service';
import { TenantUserService } from '../../user/services/tenant-user.service';
import { ContextLocation } from '../models/context-location.enum';
import { ConversationAnchor, ConversationViewModel } from '../models/conversation.model';
import { CommentService } from '../services/comment.service';
import { ConversationService } from '../services/conversation.service';

interface ConversationSidebarState extends ConversationListState {
  anchors?: ConversationAnchor[];
  location?: ContextLocation;
}

@Injectable()
export class ConversationSidebarStore extends ConversationListStore<ConversationSidebarState> {
  protected conversationService: ConversationService;
  protected commentService: CommentService;
  protected labelServce: LabelService;
  protected tenantUserService = inject(TenantUserService);

  public anchors$ = this.select((state) => state.anchors);
  public loading$ = this.select((state) => state.loading);

  public pendingConversation$ = this.select(this.store.select(ConversationSelectors.pendingConversation), (p) => p);

  public placeholderMessage$ = this.select(this.anchors$, (anchors) =>
    anchors?.length
      ? 'No related notes found'
      : 'Related Notes are not supported on this page. Visit pages such as Deal, Market Compare, or Deal Compare to view related notes here.'
  );

  private location$ = this.select((state) => state.location);

  private reloadTrigger$ = new Subject<void>();

  constructor() {
    const store = inject(Store);
    const webSocketService = inject(WebSocketService);
    const conversationService = inject(ConversationService);
    const commentService = inject(CommentService);
    const labelServce = inject(LabelService);

    super(store, webSocketService, conversationService, commentService, labelServce);
    this.conversationService = conversationService;
    this.commentService = commentService;
    this.labelServce = labelServce;

    this.patchState({
      loading: true,
    });

    this.downloadByLocationEffect$(this.location$);
    this.downloadConversationsEffect$(
      merge(this.reloadTrigger$, this.anchors$.pipe(distinctUntilChanged((a, b) => isEqual(a, b)))).pipe(
        map(() => this.get().anchors)
      )
    );
  }

  protected initWebSocket() {
    if (this.websocketId) {
      this.wsEffect$(
        this.webSocketService.subscribe(this.websocketId, 'all', {
          ignoreTabId: true,
        })
      );
    }
  }

  public close() {
    this.store.dispatch(AppActions.ToggleConversationSidebar({ open: false }));
  }

  public resetPendingConversation() {
    this.store.dispatch(ConversationActions.ClearPendingConversation());
  }

  public ngrxOnStateInit() {}

  private downloadConversationsEffect$ = this.effect((anchors$: Observable<ConversationAnchor[]>) =>
    anchors$.pipe(
      tap(() => {
        this.loadList();
      })
    )
  );

  private downloadByLocationEffect$ = this.effect((location$: Observable<ContextLocation>) =>
    location$.pipe(
      switchMap((contextLocation) => {
        if (!contextLocation) {
          return of(null);
        }

        return this.store.select(ConversationSelectors.anchorsByContextLocation(contextLocation));
      }),
      map((anchors) => {
        this.patchState({ anchors: anchors?.anchors as ConversationAnchor[] | undefined });
      })
    )
  );

  public fetchList(): Observable<ConversationViewModel[]> {
    this.patchState({ loading: !this.get().startDate });
    const anchors = this.get().anchors;

    const stream$ = !anchors?.length ? of([]) : this.conversationService.getConversationsByAnchors(anchors, 'or');

    return stream$.pipe(
      map((conversations) => {
        if (conversations.length) {
          this.store.dispatch(
            ConversationActions.AppendConversationCounts({
              anchoredConversation: conversations.map((c) => ({
                _id: c._id,
                anchorIdentifiers: c.anchors?.map((a) => `${a.type}/${a.id}`),
              })),
            })
          );
        }

        return conversations;
      })
    );
  }

  public loadByLocation() {
    const location = this.conversationService.getContextLocationByRoute(window.location.pathname);

    if (this.get().location === location) {
      this.reloadTrigger$.next(undefined);
    } else {
      this.patchState({
        location: location,
        startDate: undefined,
        entities: [],
      });
    }
  }
}
